import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { inventoryByProductByStatus } from "../../../graphql/queries";
import {
  updateProduct,
  deleteProduct,
  deleteProductStats,
} from "../../../graphql/mutations";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { Button, Confirm, useNotify } from "react-admin";

import { DeleteIcon } from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import { deepRedirect } from "../../../utils/utils";
import { ResourceType } from "../../../utils/constants";
import { sanitizeProductEdit } from "../../../graphql/account/accountCRUD";
import { isMissingProductStatsError } from "./ProductUtils";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

export const DeleteInactivateButton = ({
  record,
  history,
  backPath,
  className,
  isSaving,
  isDeleting,
  setIsDeleting,
  ...props
}) => {
  const notify = useNotify();
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const handleDelete = async (event) => {
    setIsDeleting(true);

    try {
      // Check if product is in use
      const searchInput = {
        reloadYardId: window.appUser.reloadYardId,
        productIdStatus: {
          beginsWith: {
            productId: record.id,
          },
        },
        limit: 1,
      };

      const response = await API.graphql(
        graphqlOperation(inventoryByProductByStatus, searchInput),
      );

      const total = response.data.inventoryByProductByStatus.items?.length || 0;

      // ProductStats.id === Product.id, so clear the companion stats row
      // before either deleting or inactivating the Product.
      try {
        await API.graphql(
          graphqlOperation(deleteProductStats, {
            input: { id: record.id },
          }),
        );
      } catch (statsError) {
        // Ignore missing ProductStats rows. Some products may predate the new table.
        if (!isMissingProductStatsError(statsError)) {
          logError(
            "products:DeleteInactivateButton:handleDelete:deleteProductStats",
            statsError,
            false,
          );
          throw statsError;
        }
      }

      if (!total || total === 0) {
        await API.graphql(
          graphqlOperation(deleteProduct, {
            input: { id: record.id },
          }),
        );

        const historyNote = {
          parentId: record.accountId,
          parentType: ResourceType.ACCOUNT,
          text: `Product ${record.name} deleted.`,
        };

        logHistory(historyNote);
      } else {
        // Update product active = false
        const productToUpdate = { ...record };
        productToUpdate.active = "false";

        const sanitizedProduct = sanitizeProductEdit(productToUpdate);

        await API.graphql(
          graphqlOperation(updateProduct, {
            input: sanitizedProduct,
          }),
        );

        const historyNote = {
          parentId: record.id,
          parentType: ResourceType.PRODUCT,
          text: "Product deleted.",
        };

        logHistory(historyNote);
      }

      notify(
        !total || total === 0
          ? "Product deleted successfully"
          : "Product inactivated successfully",
        "success",
      );

      deepRedirect(history, backPath);
    } catch (error) {
      logError("products:DeleteInactivateButton:handleDelete", error, notify);
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        {...props}
        label="Delete"
        disabled={isDeleting || isSaving}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title={"Delete Confirmation"}
        content={"Are you sure you want to delete this Product?"}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
